/* eslint-disable max-len */
import React, { createContext, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Dialog, IconButton, MobileStepper, Slide, SlideProps, Theme, Toolbar } from '@mui/material'
import { ArrowBack, Close } from '@mui/icons-material'
import { getPlans } from '../../data/plans'
import useWindowSize from '../../hooks/useWindowSize'
import logo from '../../assets/icons/hr2.png'
import { LoanContextType, Plan } from '../../@types'
import AdditionalDetails from './AdditionalDetails'
import ChoosePlan from './ChoosePlan'
import ConsentForm from './ConsentForm'
import Login from './Login'
import OTP from './OTP'
import Payment from './Payment'
import PersonalDetails from './PersonalDetails'
import BeforeExitDialog from './BeforeExitDialog'

const useStyles = makeStyles(({ breakpoints, spacing }: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: 660,
    width: 400,
    [breakpoints.down('sm')]: {
      height: '100%',
      width: '100%',
      alignSelf: 'center',
    },
  },

  appBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(2),
    zIndex: 3,
    border: 0,
  },
  logoContainer: {
    display: 'flex',
    fontWeight: 'bold',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    userSelect: 'none',
    '& > div': {
      letterSpacing: '1.5px',
      fontSize: 24,
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  icon: {
    width: 25,
    '& svg': {
      width: 25,
      height: 25,
    },
  }
}), { name: 'LoanJourney' })

// eslint-disable-next-line react/display-name
const Transition = React.forwardRef<unknown, SlideProps>((props, ref) => (<Slide direction="up" ref={ref} {...props} />))

export const loanContext = createContext<LoanContextType>({} as LoanContextType)

const LoanJourney = (): JSX.Element => {
  const classes = useStyles()
  const isAfterOtp = window.location.search.indexOf('otp=true')
  const [open, setOpen] = useState<boolean>(true)
  const [isCloseDialog, setIsCloseDialog] = useState(false)
  const [stage, setStage] = useState<number>(isAfterOtp !== -1 ? 2 : 0)
  const [plans] = useState<Array<Plan>>(() => getPlans())
  const [loanPlan, setLoanPlan] = useState(() => plans[0])
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [phone, setPhone] = useState<string>(window.localStorage.getItem('phone_number') ?? '')
  const [otp, setOtp] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [fullNameEnglish, setFullNameEnglish] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [nationalId, setNationalId] = useState<string>('')
  const [birthday, setBirthday] = useState<string>('')
  const [gender, setGender] = useState<string>('')
  const [familyStatus, setFamilyStatus] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [isConsentRead, setIsConsentRead] = useState<boolean>(false)
  const [isConfirmTerms, setIsConfirmTerms] = useState<boolean>(false)
  const [creditCard, setCreditCard] = useState('')
  const [creditCardValidity, setCreditCardValidity] = useState<any>(null)
  const [creditCardBackNumbers, setCreditCardBackNumbers] = useState('')
  const [accountNumber, setAccountNumber] = useState<string>('')
  const [branchNumber, setBranchNumber] = useState<string>('')
  const [bankNumber, setBankNumber] = useState<string>('')

  const contextProviderValue = {
    setOpen,
    stage,
    setStage,
    loanPlan,
    setLoanPlan,
    errorMessage,
    setErrorMessage,
    isLoading,
    setIsLoading,
    phone,
    setPhone,
    otp,
    setOtp,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    fullNameEnglish,
    setFullNameEnglish,
    email,
    setEmail,
    nationalId,
    setNationalId,
    birthday,
    setBirthday,
    gender,
    setGender,
    familyStatus,
    setFamilyStatus,
    city,
    setCity,
    address,
    setAddress,
    isConsentRead,
    setIsConsentRead,
    isConfirmTerms,
    setIsConfirmTerms,
    plans,
    creditCard,
    setCreditCard,
    creditCardValidity,
    setCreditCardValidity,
    creditCardBackNumbers,
    setCreditCardBackNumbers,
    accountNumber,
    setAccountNumber,
    branchNumber,
    setBranchNumber,
    bankNumber,
    setBankNumber,
  }

  const handleCloseDialog = (): void => {
    setIsCloseDialog(true)
  }

  const backClicked = (): void => {
    if (stage === 0) return handleCloseDialog()

    setStage(Math.floor(stage - 1))
    setErrorMessage('')
    setIsLoading(false)
  }

  const windowSize = useWindowSize()

  return (
    <loanContext.Provider value={contextProviderValue}>
      <div className={classes.root}>
        <Dialog
          open={open}
          TransitionComponent={Transition}
          fullScreen={Number(windowSize.width) < 600 ? true : false}
        >
          <div>
            <Toolbar className={classes.appBar}>
              <div>
                {stage <= 5 && stage > 0 && !isCloseDialog &&
                  <IconButton
                    className={classes.icon}
                    edge='start'
                    color='inherit'
                    onClick={backClicked}
                    aria-label="close"
                  >
                    <ArrowBack />
                  </IconButton>
                }
              </div>
              <div className={classes.logoContainer}>
                <img src={logo} alt='logo' width='180' />
              </div>
              <IconButton
                className={classes.icon}
                edge='start'
                color='inherit'
                aria-label="close"
                onClick={handleCloseDialog}
              >
                {!isCloseDialog && <Close />}
              </IconButton>
            </Toolbar>
            {stage >= 2 && !isCloseDialog &&
              <MobileStepper
                variant='progress'
                steps={5}
                position='static'
                activeStep={stage - 2}
                sx={{ display: 'flex', flex: 1, justifyContent: 'center', background: 'none' }}
                nextButton={null}
                backButton={null}
              />
            }
          </div>
          {isCloseDialog && (
            <div className={classes.dialogContent}>
              <BeforeExitDialog closeDialog={() => setOpen(false)} cancelCloseDialog={() => setIsCloseDialog(false)} />
            </div>)}
          {!isCloseDialog && (
            <div className={classes.dialogContent}>
              {stage === 0 && <Login />}
              {stage === 1 && <OTP />}
              {stage === 2 && phone && <PersonalDetails />}
              {stage === 3 && phone && <AdditionalDetails />}
              {stage === 4 && phone && <ConsentForm />}
              {stage === 5 && phone && <Payment />}
              {stage === 6 && phone && <ChoosePlan />}
            </div>)}
        </Dialog>
      </div>
    </loanContext.Provider>
  )
}

export default LoanJourney
